import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lightbulb, Check, RefreshCw, Tag } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  feature: "border-[#34C759] text-[#34C759]",
  bug: "border-[#FF3B30] text-[#FF3B30]",
  balance: "border-[#FFCC00] text-[#FFCC00]",
  ui: "border-[#00F0FF] text-[#00F0FF]",
  general: "border-white/30 text-white/60",
};

/**
 * Admin viewer for player-submitted "How can we be better?" feedback.
 * Shows OPEN tips by default; admins can mark closed once acted on.
 */
export default function AdminFeedbackList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closingId, setClosingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/feedback?status=open`, {
        withCredentials: true,
      });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load feedback");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const close = async (feedback_id) => {
    setClosingId(feedback_id);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/feedback/${feedback_id}/status`,
        { status: "closed" },
        { withCredentials: true },
      );
      toast.success("Marked closed");
      setItems((prev) => prev.filter((x) => x.feedback_id !== feedback_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally {
      setClosingId(null);
    }
  };

  return (
    <section
      className="border border-white/10 bg-black/40 backdrop-blur-xl"
      data-testid="admin-feedback-list"
    >
      <div className="p-4 border-b border-white/10 flex items-center gap-3">
        <Lightbulb className="w-4 h-4 text-[#FF3B30]" />
        <div className="flex-1">
          <div className="label-kicker">Player Voices</div>
          <div className="font-heading text-xl uppercase">Feedback Inbox</div>
        </div>
        <span
          className={`px-2 py-0.5 text-[10px] tracking-[0.25em] uppercase border ${
            items.length > 0
              ? "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/10"
              : "border-white/15 text-white/40"
          }`}
          data-testid="admin-feedback-count"
        >
          {items.length} open
        </span>
        <button
          onClick={load}
          disabled={loading}
          data-testid="admin-feedback-refresh"
          className="p-1.5 border border-white/10 hover:border-white/30 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {loading && items.length === 0 ? (
        <div className="p-8 text-center text-white/40 text-[11px] tracking-[0.3em] uppercase">
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-white/40 text-sm" data-testid="admin-feedback-empty">
          Nothing open. Players are happy (or quiet).
        </div>
      ) : (
        <div className="divide-y divide-white/5 max-h-[60vh] overflow-y-auto">
          {items.map((item) => {
            const busy = closingId === item.feedback_id;
            const catCls = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.general;
            return (
              <div
                key={item.feedback_id}
                className="px-4 py-4"
                data-testid={`admin-feedback-row-${item.feedback_id}`}
              >
                <div className="flex items-start gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 text-[10px] tracking-wider uppercase font-mono text-white/40">
                      <span className={`px-1.5 py-0.5 border ${catCls} flex items-center gap-1`}>
                        <Tag className="w-2.5 h-2.5" />
                        {item.category}
                      </span>
                      <span>{item.user_name || "Unknown"}</span>
                      <span className="text-white/20">·</span>
                      <span>{new Date(item.submitted_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap break-words">
                      {item.text}
                    </div>
                  </div>
                  <button
                    onClick={() => close(item.feedback_id)}
                    disabled={busy}
                    data-testid={`admin-feedback-close-${item.feedback_id}`}
                    className={`p-2 border-2 transition-colors flex-shrink-0 ${
                      busy
                        ? "border-white/10 text-white/30 cursor-not-allowed"
                        : "border-[#34C759] text-[#34C759] hover:bg-[#34C759] hover:text-black"
                    }`}
                    title="Mark closed"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
